/**
 * Marketplace header search autocomplete (Blade).
 *
 * Mirrors Vue `Autocomplete.vue` (identifiant="autocomplete") wired to InstantSearch
 * on `#header-searchbox-mobile` — recent searches, iconic brands, query suggestions.
 */

import { markListingSearchControls, markListingSearchPanel } from "./listing-tracking";
import { FEATURED_BRANDS } from "../shared/brand-logos";
import { autocomplete } from "@algolia/autocomplete-js";
import { createLocalStorageRecentSearchesPlugin } from "@algolia/autocomplete-plugin-recent-searches";
import { connectSearchBox } from "instantsearch.js/es/connectors";
import type { InstantSearch } from "instantsearch.js";


import {
    SearchClientFactory,
    type SearchConfig,
} from "../services/SearchClientFactory";
import { redirectToMarketplace } from "../shared/autocomplete-factory";
import {
    clearPillSearchPanelOpenState,
    pillSearchPanelLiantOptions,
} from "../shared/pill-search-panel-liant";
import { installAutocompleteScrollGuard } from "../shared/autocomplete-scroll-guard";
import { installCompactSearchBehavior, isCompactSearch } from "./compact-search-behavior";

/** Must match Vue MarketplaceHeader `identifiant="autocomplete"`. */
const AUTOCOMPLETE_IDENT = "autocomplete";
const RECENT_SEARCHES_STORAGE_KEY = `instantsearch-${AUTOCOMPLETE_IDENT}`;

const brandData = FEATURED_BRANDS;

function getRecentSearchesCount(recentSearchesPlugin: {
    data?: { getAll: () => unknown };
}): number {
    if (recentSearchesPlugin.data) {
        const recentSearches = recentSearchesPlugin.data.getAll();
        return Array.isArray(recentSearches) ? recentSearches.length : 0;
    }
    return 0;
}

function hasWordStartingWith(text: string, query: string): boolean {
    if (!text || !query) return false;
    const textLower = text.toLowerCase();
    const queryLower = query.toLowerCase().trim();
    if (!queryLower.includes(" ")) {
        return textLower
            .split(/\s+/)
            .some((word) => word.startsWith(queryLower));
    }
    const queryWords = queryLower.split(/\s+/);
    const textWords = textLower.split(/\s+/);
    for (let i = 0; i <= textWords.length - queryWords.length; i++) {
        let allMatch = true;
        for (let j = 0; j < queryWords.length; j++) {
            const textWord = textWords[i + j];
            const queryWord = queryWords[j];
            if (j === 0) {
                if (
                    !textWord.includes(queryWord) &&
                    !textWord.startsWith(queryWord)
                ) {
                    allMatch = false;
                    break;
                }
            } else if (!textWord.startsWith(queryWord)) {
                allMatch = false;
                break;
            }
        }
        if (allMatch) return true;
    }
    return false;
}

function findWordMatch(
    text: string,
    query: string,
): { start: number; end: number } | null {
    if (!text || !query) return null;
    const textLower = text.toLowerCase();
    const queryLower = query.toLowerCase().trim();
    if (!queryLower.includes(" ")) {
        const words = textLower.split(/\s+/);
        let currentIndex = 0;
        for (const word of words) {
            const wordStartIndex = textLower.indexOf(word, currentIndex);
            if (word.startsWith(queryLower)) {
                return {
                    start: wordStartIndex,
                    end: wordStartIndex + queryLower.length,
                };
            }
            currentIndex = wordStartIndex + word.length;
        }
        return null;
    }
    const queryWords = queryLower.split(/\s+/);
    const firstQueryWord = queryWords[0];
    const firstWordIndex = textLower.indexOf(firstQueryWord);
    if (firstWordIndex === -1) return null;
    return {
        start: firstWordIndex,
        end: Math.min(firstWordIndex + queryLower.length, textLower.length),
    };
}

function getShortestMatchingWordLength(text: string, query: string): number {
    if (!text || !query) return Infinity;
    const textLower = text.toLowerCase();
    const queryLower = query.toLowerCase().trim();
    if (!queryLower.includes(" ")) {
        let shortestLength = Infinity;
        for (const word of textLower.split(/\s+/)) {
            if (word.startsWith(queryLower)) {
                shortestLength = Math.min(shortestLength, word.length);
            }
        }
        return shortestLength;
    }
    return hasWordStartingWith(textLower, queryLower) ? queryLower.length : Infinity;
}

type AutocompleteDisplayOptions = {
    /** false: not the header pill search (no header open state, Algolia panel positioning). */
    headerPanel?: boolean;
    /** Autocomplete.vue « Rechercher » button (hero search box). */
    searchButton?: boolean;
};

type MarketplaceAutocompleteCallbacks = {
    onQueryChange: (query: string) => void;
    onQuerySelect: (query: string) => void;
    onMakeSelect: (make: string) => void;
};

/**
 * InstantSearch searchBox refine is gated: only armed commits (submit / select / reset)
 * reach Typesense. Keyboard preview via autocomplete `completion` must never trigger
 * a search — do not sync query on autocomplete `onStateChange`.
 */
export function createCommitGatedQueryHook() {
    let allowNextRefine = false;

    return {
        armRefine: () => {
            allowNextRefine = true;
        },
        queryHook: (query: string, search: (value: string) => void) => {
            if (!allowNextRefine) {
                return;
            }
            allowNextRefine = false;
            search(query);
        },
    };
}

function createQuerySuggestionsPlugin(
    autocompleteSearchClient: any,
    indexName: string,
    callbacks: MarketplaceAutocompleteCallbacks,
) {
    return {
        getSources({ query }: { query: string }) {
            if (!query) return [];

            return [
                {
                    sourceId: "querySuggestionsPlugin",
                    async getItems() {
                        try {
                            const results = await autocompleteSearchClient.search([
                                {
                                    indexName,
                                    params: {
                                        query,
                                        hitsPerPage: 50,
                                        attributesToRetrieve: [
                                            "suggestion_make",
                                            "suggestion_make_model",
                                            "suggestion_make_model_specification",
                                        ],
                                    },
                                },
                            ]);

                            const suggestions: Array<{ query: string }> = [];
                            const seenQueries = new Set<string>();
                            const queryLower = query.toLowerCase();

                            const makeSuggestions: Array<{ query: string }> = [];
                            const makeModelSuggestions: Array<{ query: string }> =
                                [];
                            const makeModelSpecificationSuggestions: Array<{
                                query: string;
                            }> = [];

                            results.results[0].hits.forEach((hit: any) => {
                                if (
                                    hit.suggestion_make &&
                                    !seenQueries.has(hit.suggestion_make) &&
                                    hasWordStartingWith(
                                        hit.suggestion_make,
                                        queryLower,
                                    )
                                ) {
                                    makeSuggestions.push({
                                        query: hit.suggestion_make,
                                    });
                                    seenQueries.add(hit.suggestion_make);
                                }
                                if (
                                    hit.suggestion_make_model &&
                                    !seenQueries.has(hit.suggestion_make_model) &&
                                    hasWordStartingWith(
                                        hit.suggestion_make_model,
                                        queryLower,
                                    )
                                ) {
                                    makeModelSuggestions.push({
                                        query: hit.suggestion_make_model,
                                    });
                                    seenQueries.add(hit.suggestion_make_model);
                                }
                                if (
                                    hit.suggestion_make_model_specification &&
                                    !seenQueries.has(
                                        hit.suggestion_make_model_specification,
                                    ) &&
                                    hasWordStartingWith(
                                        hit.suggestion_make_model_specification,
                                        queryLower,
                                    )
                                ) {
                                    makeModelSpecificationSuggestions.push({
                                        query: hit.suggestion_make_model_specification,
                                    });
                                    seenQueries.add(
                                        hit.suggestion_make_model_specification,
                                    );
                                }
                            });

                            const sortByMatchLength = (
                                items: Array<{ query: string }>,
                            ) =>
                                items.sort((a, b) => {
                                    const lengthA = getShortestMatchingWordLength(
                                        a.query,
                                        queryLower,
                                    );
                                    const lengthB = getShortestMatchingWordLength(
                                        b.query,
                                        queryLower,
                                    );
                                    if (lengthA !== lengthB) return lengthA - lengthB;
                                    return a.query.localeCompare(b.query);
                                });

                            suggestions.push(...sortByMatchLength(makeSuggestions));
                            suggestions.push(
                                ...sortByMatchLength(makeModelSuggestions),
                            );
                            suggestions.push(
                                ...sortByMatchLength(
                                    makeModelSpecificationSuggestions,
                                ),
                            );
                            return suggestions.slice(0, 7);
                        } catch {
                            return [];
                        }
                    },
                    getItemInputValue({ item }: { item: { query: string } }) {
                        return item.query;
                    },
                    onSelect({ item }: { item: { query: string } }) {
                        callbacks.onQuerySelect(item.query);
                    },
                    templates: {
                        item({
                            item,
                            html,
                            state,
                        }: {
                            item: { query: string };
                            html: any;
                            state: { query?: string };
                        }) {
                            const formattedQuery = item.query;
                            const userInput = (state.query || "").toLowerCase();
                            if (!userInput) {
                                return html`<span class="aa-ItemContentTitle">${formattedQuery}</span>`;
                            }
                            const wordMatch = findWordMatch(
                                formattedQuery,
                                userInput,
                            );
                            if (!wordMatch) {
                                return html`<span class="aa-ItemContentTitle">${formattedQuery}</span>`;
                            }
                            const beforeMatch = formattedQuery.substring(
                                0,
                                wordMatch.start,
                            );
                            const match = formattedQuery.substring(
                                wordMatch.start,
                                wordMatch.end,
                            );
                            const afterMatch = formattedQuery.substring(
                                wordMatch.end,
                            );
                            return html`<span class="aa-ItemContentTitle">
                                ${beforeMatch}<mark>${match}</mark>${afterMatch}
                            </span>`;
                        },
                    },
                },
            ];
        },
    };
}

function createRecentSearchesPlugin(callbacks: MarketplaceAutocompleteCallbacks) {
    return createLocalStorageRecentSearchesPlugin({
        key: RECENT_SEARCHES_STORAGE_KEY,
        limit: 3,
        transformSource({ source }) {
            return {
                ...source,
                getItems(params: any) {
                    if (params.query && params.query.length > 0) return [];
                    return source.getItems(params);
                },
                onSelect({ item }: { item: { label: string } }) {
                    callbacks.onQuerySelect(item.label);
                },
                templates: {
                    ...source.templates,
                    item(params: { item: { label: string }; html: any }) {
                        const { item, html } = params;
                        return html`<span class="aa-ItemLink">${item.label}</span>`;
                    },
                },
            };
        },
    });
}

function initMarketplaceHeaderAutocomplete(
    containerSelector: string,
    placeholder: string,
    searchConfig: SearchConfig,
    initialQuery: string,
    callbacks: MarketplaceAutocompleteCallbacks,
    redirectToMarketplaceOnSelect: boolean,
    display: AutocompleteDisplayOptions = {},
): ReturnType<typeof autocomplete> | null {
    const container = document.querySelector(containerSelector);
    if (!container) return null;

    installAutocompleteScrollGuard();

    container.innerHTML = "";

    // Téléphone : dernier texte cherché (pas de nouvelle recherche identique au ✓), et
    // clavier fermé après un choix pour laisser voir les résultats (compact-search-behavior).
    let lastCommitted = initialQuery;
    let releaseFocus = () => {};
    const outerCallbacks = callbacks;
    callbacks = {
        onQueryChange: (query) => {
            lastCommitted = query;
            outerCallbacks.onQueryChange(query);
        },
        onQuerySelect: (query) => {
            lastCommitted = query;
            outerCallbacks.onQuerySelect(query);
            releaseFocus();
        },
        onMakeSelect: (make) => {
            outerCallbacks.onMakeSelect(make);
            releaseFocus();
        },
    };

    const recentSearchesPlugin = createRecentSearchesPlugin(callbacks);
    const plugins: any[] = [recentSearchesPlugin];

    if (searchConfig.driver === "typesense") {
        try {
            const autocompleteSearchClient =
                SearchClientFactory.createAutocompleteClient(searchConfig);
            plugins.push(
                createQuerySuggestionsPlugin(
                    autocompleteSearchClient,
                    searchConfig.indexName,
                    callbacks,
                ),
            );
        } catch {
            // Recent searches still work without suggestions
        }
    }

    const instance = autocomplete({
        container: containerSelector,
        placeholder,
        detachedMediaQuery: "none",
        openOnFocus: true,
        ...(display.headerPanel === false ? {} : pillSearchPanelLiantOptions()),
        initialState: { query: initialQuery },
        plugins,
        onSubmit({ state }) {
            if (redirectToMarketplaceOnSelect) {
                lastCommitted = state.query;
                redirectToMarketplace({ query: state.query });
                return;
            }
            callbacks.onQueryChange(state.query);
            releaseFocus();
        },
        onReset() {
            callbacks.onQueryChange("");
        },
        // Texte effacé au clavier (sélection puis suppression) : même effet que la croix, la recherche et ses
        // filtres partent. Seul le champ vide est pris en compte : une saisie en cours ne lance rien.
        onStateChange({ state, prevState }) {
            if (state.query === "" && prevState.query !== "" && lastCommitted !== "" && !redirectToMarketplaceOnSelect) {
                callbacks.onQueryChange("");
            }
        },
        getSources(): any {
            return [
                {
                    sourceId: "brands",
                    getItems({ query }: any) {
                        // Téléphone : rien d'imposé au toucher, les pastilles de marques de la page suffisent
                        if ((query && query.length > 0) || isCompactSearch()) return [];
                        return brandData.filter(({ label }) =>
                            label
                                .toLowerCase()
                                .includes((query || "").toLowerCase()),
                        );
                    },
                    onSelect({ item }: any) {
                        callbacks.onMakeSelect(item.label);
                    },
                    templates: {
                        item({ item, html }: any) {
                            return html`<span class="aa-ItemLink">
                                <img
                                    class="aa-Item--icon"
                                    src="${item.img}"
                                    alt="${item.label}"
                                />
                                ${item.label}
                            </span>`;
                        },
                    },
                },
            ];
        },
        render({ elements, render, html, state }, root) {
            // Mesure PostHog : toute suggestion cliquée dans ce panneau (récente, marque, suggestion)
            markListingSearchPanel(root);
            const {
                recentSearchesPlugin: recentEl,
                querySuggestionsPlugin: suggestionsEl,
                brands,
            } = elements;
            const recentSearchesCount =
                getRecentSearchesCount(recentSearchesPlugin);

            if (!state.query && isCompactSearch()) {
                // Téléphone : seulement les recherches récentes (le panneau ne s'ouvre pas sans)
                render(
                    html`<div class="aa-PanelLayout aa-Panel--scrollable">
                        <div class="aa-PanelSections">
                            <div>
                                <div class="aa-SourceHeader">
                                    <span class="aa-SourceHeaderTitle">
                                        Recherches récentes
                                    </span>
                                </div>
                                ${recentEl}
                            </div>
                        </div>
                    </div>` as any,
                    root,
                );
            } else if (!state.query) {
                render(
                    html`<div class="aa-PanelLayout aa-Panel--scrollable">
                        <div class="aa-PanelSections">
                            ${recentSearchesCount > 0
                                ? html`<div>
                                      <div class="aa-SourceHeader">
                                          <span class="aa-SourceHeaderTitle">
                                              Recherches récentes
                                          </span>
                                      </div>
                                      ${recentEl}
                                  </div>`
                                : ""}
                            <div>
                                <div class="aa-SourceHeader">
                                    <span class="aa-SourceHeaderTitle"
                                        >Marques emblématiques</span
                                    >
                                </div>
                                <div class="aa-PanelSectionBrands">
                                    ${brands}
                                </div>
                            </div>
                        </div>
                    </div>` as any,
                    root,
                );
            } else {
                render(
                    html`<div class="aa-PanelLayout aa-Panel--scrollable">
                        <div class="aa-PanelSections">
                            <div>${suggestionsEl || ""}</div>
                        </div>
                    </div>` as any,
                    root,
                );
            }
        },
    });

    if (display.searchButton) appendSearchButton(container);
    // Mesure PostHog : champ, loupe, croix et « Rechercher » rendus par l'autocomplétion
    markListingSearchControls(container);

    ({ releaseFocus } = installCompactSearchBehavior({
        container,
        close: () => instance.setIsOpen(false),
        commit: (query) => {
            if (redirectToMarketplaceOnSelect) {
                lastCommitted = query;
                redirectToMarketplace({ query });
                return;
            }
            callbacks.onQueryChange(query);
        },
        lastCommitted: () => lastCommitted,
    }));

    return instance;
}

/**
 * Autocomplete.vue appends a « Rechercher » button (type="button") to its form: it only
 * focuses the input, Enter submits.
 */
export function appendSearchButton(container: Element): void {
    const form = container.querySelector<HTMLElement>(".aa-Form");
    const input = container.querySelector<HTMLInputElement>(".aa-Input");
    if (!form || !input || form.querySelector(".aa-SearchButton")) return;
    const searchBtn = document.createElement("button");
    searchBtn.type = "button";
    searchBtn.className = "aa-SearchButton";
    searchBtn.textContent = "Rechercher";
    searchBtn.addEventListener("click", () => input.focus());
    form.appendChild(searchBtn);
}

export function createMarketplaceHeaderAutocompleteWidget(
    instantSearch: InstantSearch,
    searchConfig: SearchConfig,
    options: {
        container: string;
        placeholder: string;
        redirectToMarketplaceOnSelect?: boolean;
        /**
         * Autocomplete.vue updateAlgoliaState on Vehicle/Index (curated SEO search): an
         * iconic brand sets `menu: { make }` in the InstantSearch state instead of leaving
         * for /marketplace (the curated drift guard then decides, like Marketplace.vue).
         */
        brandSetsUiState?: boolean;
        /**
         * Marketplace : avant de chercher (texte vide compris), la page peut partir directement vers la recherche
         * avec ses filtres, ou retirer les filtres de la recherche précédente en même temps (search-query-filters.ts).
         * Renvoie true quand c'est réglé : rien n'est cherché ici.
         */
        resolveBeforeCommit?: (query: string) => Promise<boolean>;
    } & AutocompleteDisplayOptions,
) {
    const redirectToMarketplaceOnSelect = Boolean(
        options.redirectToMarketplaceOnSelect,
    );
    let autocompleteInstance: ReturnType<typeof autocomplete> | null = null;
    let syncedQuery = "";
    let commitVersion = 0;
    const { armRefine, queryHook } = createCommitGatedQueryHook();

    const indexName = searchConfig.indexName;

    const setMakeFilter = (make: string) => {
        instantSearch.setUiState((uiState) => ({
            ...uiState,
            [indexName]: {
                ...uiState[indexName],
                page: 1,
                menu: {
                    ...(uiState[indexName]?.menu ?? {}),
                    make,
                },
            },
        }));
    };

    return connectSearchBox(({ query, refine }, isFirstRender) => {
        const commitQuery = (nextQuery: string) => {
            // Même texte que la recherche en cours (croix : effacement puis remise à zéro) : rien à relancer
            const currentQuery = (instantSearch.mainIndex.getHelper()?.state.query ?? "").trim();
            if (nextQuery.trim() === currentQuery && nextQuery === syncedQuery) return;
            syncedQuery = nextQuery;
            const version = ++commitVersion;
            const run = () => {
                armRefine();
                refine(nextQuery);
            };
            // Pas d'état intermédiaire (résultats sans les filtres) avant le départ vers la recherche filtrée
            if (options.resolveBeforeCommit) {
                void options.resolveBeforeCommit(nextQuery).then((handled) => {
                    // Une recherche plus récente a été envoyée entre-temps : c'est elle qui compte
                    if (!handled && version === commitVersion) run();
                });
                return;
            }
            run();
        };

        const callbacks: MarketplaceAutocompleteCallbacks = {
            onQueryChange: commitQuery,
            onQuerySelect: (nextQuery) => {
                if (redirectToMarketplaceOnSelect) {
                    redirectToMarketplace({ query: nextQuery });
                    return;
                }
                autocompleteInstance?.setQuery(nextQuery);
                commitQuery(nextQuery);
            },
            onMakeSelect: (make) => {
                if (options.brandSetsUiState) {
                    instantSearch.setUiState((uiState) => ({
                        ...uiState,
                        [indexName]: { ...uiState[indexName], page: 1, menu: { make } },
                    }));
                    return;
                }
                if (redirectToMarketplaceOnSelect) {
                    redirectToMarketplace({ make });
                    return;
                }
                setMakeFilter(make);
            },
        };

        if (isFirstRender) {
            autocompleteInstance = initMarketplaceHeaderAutocomplete(
                options.container,
                options.placeholder,
                searchConfig,
                query,
                callbacks,
                redirectToMarketplaceOnSelect,
                { headerPanel: options.headerPanel, searchButton: options.searchButton },
            );
            syncedQuery = query;
            return;
        }

        if (autocompleteInstance && query !== syncedQuery) {
            syncedQuery = query;
            autocompleteInstance.setQuery(query);
        }
    })({ queryHook });
}
